/**
 * Picture and timing layout of the test-signal source, shared by the source
 * (which draws it) and by every probe that reads it back from program pixels,
 * the WebRTC receiver or a recording. Pure data and arithmetic only, so a test
 * can pass `TEST_SIGNAL_LAYOUT` into a page as JSON and decode there.
 *
 * The counter strip is a row of 32 equal cells across the top of the picture:
 * cell 0 is a white reference, cell 1 a black reference, cells 2..21 the frame
 * number in Gray code (one cell changes per frame, so a frame caught mid-update
 * still decodes to one of its two neighbours), cell 22 the parity of those 20
 * bits, and cells 23 and 24 the A/V-sync flash. This is the same layout the
 * reliability rig's plugin fixture uses.
 */

export const TEST_SIGNAL_LAYOUT = {
  cells: 32,
  counterBits: 20,
  cell: { white: 0, black: 1, firstBit: 2, parity: 22, flash: 23, flashCopy: 24 },
  /** Strip height as a fraction of the picture height, and its floor in pixels. */
  stripFraction: 0.06,
  minStripPx: 8,
  /** How long the flash cells stay white after each period starts. */
  flashHoldMs: 200,
} as const;

export const COUNTER_MODULO = 2 ** TEST_SIGNAL_LAYOUT.counterBits;

export function stripHeight(pictureHeight: number): number {
  return Math.min(pictureHeight, Math.max(TEST_SIGNAL_LAYOUT.minStripPx, Math.round(pictureHeight * TEST_SIGNAL_LAYOUT.stripFraction)));
}

export function toGray(n: number): number {
  return n ^ (n >>> 1);
}

export function fromGray(g: number): number {
  let n = g;
  for (let shift = 1; shift < 32; shift <<= 1) n ^= n >>> shift;
  return n >>> 0;
}

/** White/black state of every strip cell for `counter`, with the flash cells set by `flash`. */
export function encodeStrip(counter: number, flash: boolean): boolean[] {
  const { cells, counterBits, cell } = TEST_SIGNAL_LAYOUT;
  const out = new Array<boolean>(cells).fill(false);
  out[cell.white] = true;
  const gray = toGray(((counter % COUNTER_MODULO) + COUNTER_MODULO) % COUNTER_MODULO);
  let parity = false;
  for (let bit = 0; bit < counterBits; bit++) {
    const on = ((gray >>> bit) & 1) === 1;
    out[cell.firstBit + bit] = on;
    if (on) parity = !parity;
  }
  out[cell.parity] = parity;
  out[cell.flash] = flash;
  out[cell.flashCopy] = flash;
  return out;
}

export interface DecodedStrip {
  /** Frame number, or -1 when the strip is unreadable (references too close, parity wrong). */
  counter: number;
  flash: boolean;
}

/**
 * Reads one strip from the brightness (0..255) at the centre of each of the 32
 * cells. The threshold sits halfway between the two reference cells, so
 * scaling, colour conversion and lossy encoding do not move it.
 */
export function decodeStrip(cellLuma: ArrayLike<number>): DecodedStrip {
  const { counterBits, cell } = TEST_SIGNAL_LAYOUT;
  const white = cellLuma[cell.white];
  const black = cellLuma[cell.black];
  if (!(white - black > 64)) return { counter: -1, flash: false };
  const threshold = (white + black) / 2;
  let gray = 0;
  let parity = 0;
  for (let bit = 0; bit < counterBits; bit++) {
    if (cellLuma[cell.firstBit + bit] > threshold) {
      gray |= 1 << bit;
      parity ^= 1;
    }
  }
  const flash = cellLuma[cell.flash] > threshold;
  if ((cellLuma[cell.parity] > threshold ? 1 : 0) !== parity) return { counter: -1, flash };
  return { counter: fromGray(gray), flash };
}

/** Frame number of the source's own clock `elapsedMs` after it started, at `fps`. */
export function frameAt(elapsedMs: number, fps: number): number {
  return Math.max(0, Math.floor((elapsedMs * fps) / 1000 + 1e-6));
}

/** Start of frame `n` on the source's clock, ms. */
export function frameStartMs(n: number, fps: number): number {
  return (n * 1000) / fps;
}

/**
 * Index of the flash period frame `n` falls in when it shows the flash, else
 * -1. The first flash is one period after start, never at frame 0, so a probe
 * never sees a flash before it has seen the clean strip.
 */
export function flashIndex(n: number, fps: number, periodMs: number): number {
  if (!(periodMs > 0)) return -1;
  const t = frameStartMs(n, fps);
  const k = Math.floor(t / periodMs);
  if (k < 1) return -1;
  return t - k * periodMs < Math.min(TEST_SIGNAL_LAYOUT.flashHoldMs, periodMs / 2) ? k : -1;
}

/** Deterministic 32-bit PRNG (mulberry32): the same seed gives the same picture on every machine. */
export function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The colour bars, left to right: 75% white, yellow, cyan, green, magenta, red, blue. */
export const BAR_COLOURS: readonly (readonly [number, number, number])[] = [
  [191, 191, 191],
  [191, 191, 0],
  [0, 191, 191],
  [0, 191, 0],
  [191, 0, 191],
  [191, 0, 0],
  [0, 0, 191],
];

/**
 * Regions of the default ('bars') pattern as fractions of the picture, below
 * the strip: bars across the top two thirds, a checkerboard for sharpness in
 * the bottom-right quarter, and a white box moving left to right along the
 * bottom-left band, one pass every `MOVING_BOX_PERIOD_FRAMES` frames.
 */
export const PATTERN_REGIONS = {
  bars: { x: 0, y: 0, w: 1, h: 2 / 3 },
  checker: { x: 0.75, y: 2 / 3, w: 0.25, h: 1 / 3 },
  track: { x: 0, y: 2 / 3, w: 0.75, h: 1 / 3 },
  /** Checker square size in picture pixels. */
  checkerPx: 8,
} as const;

export const MOVING_BOX_PERIOD_FRAMES = 120;

/** Left edge of the moving box in track fractions (0..1 - box width) for frame `n`. */
export function movingBoxX(n: number, boxFraction: number): number {
  const phase = (n % MOVING_BOX_PERIOD_FRAMES) / MOVING_BOX_PERIOD_FRAMES;
  return phase * (1 - boxFraction);
}
